/**
 * React Query hook for Payment History V2 with RDS-based pagination
 * Uses the new listPaymentRecordV3 API with improved filtering and sorting
 */

import { useQuery } from '@tanstack/react-query';
import { listPaymentRecordV3 } from 'services/billingServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Fetches payment history data with RDS-based pagination
 * API Response format: { total_count, page_count, page, limit, data: [] }
 *
 * @param {Object} params - Request parameters
 * @returns {Promise<Object>} - Response with data array and pagination metadata
 */
const fetchPaymentHistoryV3 = async ({
  limit = 10,
  page = 1,
  case_id = '',
  search_key = '',
  practice_area = null,
  payment_date_lower = '',
  payment_date_upper = '',
  sort_by = '',
  sort_order = '',
}) => {
  try {
    const response = await listPaymentRecordV3({
      case_id,
      search_key,
      practice_area,
      payment_date_lower,
      payment_date_upper,
      sort_by,
      sort_order,
      limit,
      page,
    });

    if (response?.data) {
      return response.data;
    }

    // Return empty structure if no data (matching API format)
    return {
      total_count: 0,
      page_count: 0,
      page: page,
      limit: limit,
      data: [],
    };
  } catch (error) {
    console.error('Error fetching payment history V3:', error);
    throw error;
  }
};

/**
 * Hook to fetch payment history with RDS-based pagination using React Query
 *
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Items per page (default: 10)
 * @param {number} options.page - Page number (default: 1)
 * @param {string} options.case_id - Filter by case UUID
 * @param {string} options.search_key - Search keyword
 * @param {Array|null} options.practice_area - Filter by practice area IDs
 * @param {string} options.payment_date_lower - Start date filter (ISO format)
 * @param {string} options.payment_date_upper - End date filter (ISO format)
 * @param {string} options.sort_by - Sort field (default: '')
 *   Allowed values: 'created_at', 'payment_record_date', 'payment_record_amount',
 *   'paid_amount', 'case_name', 'billing_contact_name', 'payment_method'
 *   Empty string means no sorting
 * @param {string} options.sort_order - Sort direction: 'asc' or 'desc' (default: '')
 *   Empty string means no sorting
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} Query result object
 * @returns {Array} returns.paymentRecords - Array of payment record objects
 * @returns {number} returns.totalPages - Total number of pages
 * @returns {number} returns.currentPage - Current page number
 * @returns {number} returns.pageSize - Items per page
 * @returns {number} returns.totalCount - Total number of records
 * @returns {boolean} returns.isLoading - Initial loading state
 * @returns {boolean} returns.isFetching - Fetching state (including background refetch)
 * @returns {boolean} returns.isError - Error state indicator
 * @returns {Object|null} returns.error - Error object if request failed
 * @returns {Function} returns.refetch - Function to manually refetch data
 *
 * @example
 * const {
 *   paymentRecords,
 *   totalPages,
 *   isLoading,
 *   refetch
 * } = usePaymentHistoryV2Query({
 *   limit: 10,
 *   page: 1,
 *   case_id: 'case-uuid',
 *   search_key: 'search term',
 *   practice_area: ['practice-id-1', 'practice-id-2'],
 *   sort_by: 'payment_record_date',
 *   sort_order: 'desc',
 * });
 */
export const usePaymentHistoryV2Query = ({
  limit = 10,
  page = 1,
  case_id = '',
  search_key = '',
  practice_area = null,
  payment_date_lower = '',
  payment_date_upper = '',
  sort_by = '',
  sort_order = '',
  enabled = true,
} = {}) => {
  // Create stable query key based on all filter parameters including sorting
  const queryKey = queryKeys.paymentHistory.listV2(
    {
      case_id,
      search_key,
      practice_area,
      payment_date_lower,
      payment_date_upper,
      sort_by,
      sort_order,
    },
    page,
    limit
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchPaymentHistoryV3({
        limit,
        page,
        case_id,
        search_key,
        practice_area,
        payment_date_lower,
        payment_date_upper,
        sort_by,
        sort_order,
      }),
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes - serves from cache before that
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (React Query v4 uses gcTime instead of cacheTime)
    keepPreviousData: true, // Keep previous data while fetching
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: enabled, // Only refetch on mount if query is enabled
    retry: (failureCount) => {
      // Retry twice for errors
      return failureCount < 2;
    },
  });

  return {
    paymentRecords: data?.data || [],
    totalPages: data?.page_count || 0,
    currentPage: data?.page || page,
    pageSize: data?.limit || limit,
    totalCount: data?.total_count || 0,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export default usePaymentHistoryV2Query;
